/**
 * Canadian tax categories shared by the tax calculator and the bundle allocator.
 * Kept in its own module so both can classify a product the same way without
 * taxConfig and bundleTax importing each other.
 */

// Tax-exempt product categories (truly exempt - no tax charged)
export const TAX_EXEMPT_CATEGORIES = [
  'educational',
  'medical',
  'food'
];

// Product categories eligible for point-of-sale rebate in Canada
// Per CRA: https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/gst-hst-businesses/charge-collect-specific-situations/suppliers-publications.html
// Books qualify for point-of-sale rebate of provincial portion of HST - only charge 5% GST
export const POINT_OF_SALE_REBATE_CATEGORIES = [
  'book',
  'books',
  // 'bundle',  // Removed 'bundle' as it was catching merchandise bundles. Book bundles should have 'book' in name/category.
  'collection',
  'collections',
  'publication',
  'printed book',
  'audio recording' // if it's a spoken reading of a printed book
];

/** Lowercase and drop emojis so category/name matching is stable. */
export function normalizeTaxToken(value) {
  return (value || '')
    .toLowerCase()
    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
    .trim();
}

export function isDigitalAlbumItem(item) {
  return (
    item?.is_digital_album === 1 ||
    item?.is_digital_album === true ||
    item?.is_digital_album === '1'
  );
}

/**
 * Tax treatment for one product: 'digital' | 'exempt' | 'custom' | 'book' | 'standard'.
 * Mirrors the branching in calculateTax so a bundle component is taxed the same
 * whether it is bought on its own or inside a bundle.
 */
export function classifyTaxCategory(item) {
  if (!item) return 'standard';

  // Digital music downloads: full HST/GST — never book point-of-sale rebate
  if (isDigitalAlbumItem(item)) return 'digital';

  const taxType = normalizeTaxToken(item.tax_type) || 'auto';
  if (taxType === 'exempt') return 'exempt';
  if (taxType === 'gst') return 'book';
  if (taxType === 'custom' && item.tax_rate != null) return 'custom';

  if (taxType === 'auto') {
    const category = normalizeTaxToken(item.category);
    const name = normalizeTaxToken(item.name);
    const type = normalizeTaxToken(item.type);
    const matchesRebate = POINT_OF_SALE_REBATE_CATEGORIES.some(
      (rebate) => category.includes(rebate) || name.includes(rebate) || type.includes(rebate)
    );
    if (matchesRebate) return 'book';
  }

  return 'standard';
}
